import type { LiveEventSlot, PlaylistItem } from '@apm/contracts';
import { useT } from '../i18n/LocaleProvider.js';
import type { EpgRow } from '../epg.js';
import { EpgScreen } from '../screens/EpgScreen.js';

/**
 * 재생 중에 여는 **채널 편성표** (v9 신설 · v25 가 목록을 편성표로 바꿨다).
 *
 * **시트다. 화면 전환이 아니다** — 채널을 고르는 동안 영상이 계속 간다 (절대 규칙 14).
 * 편성표 화면(`/channels`)으로 옮기면 `WatchScreen` 이 내려가 리시버가 파괴되고,
 * **그냥 둘러보려던 시청자의 재생이 끊긴다.**
 *
 * 몸통은 `EpgScreen` 을 그대로 쓴다. 카드 모양이 두 벌이 되면 한쪽만 고쳐지고, 그 차이는
 * 「어디서 들어왔느냐에 따라 목록이 다르다」로 나타난다.
 *
 * **`sheet__panel--wide` 가 v25 에서 붙었다** — 기본 폭이 30rem(480px)이라 카드가 가로로
 * 서는 순간 **한 장 반**밖에 안 들어간다. 증상은 「TV 에서 카드가 몇 장 안 보인다」
 * 하나이고 **개발 노트북 창에서는 「원래 좁은 화면이구나」로 읽혀 영원히 안 드러난다**
 * (`docs/51` §5.3).
 */
export function ChannelSheet({
  rows,
  currentChannelId,
  currentProductId,
  inLive = false,
  favorites,
  onToggleFavorite,
  onPick,
  signedIn,
  onClose,
  now,
}: {
  rows: readonly EpgRow[];
  /** **재생 중인 채널. 게이트에서 열면 `null` 이다** (2026-09-06 · `watchingChannelId`) */
  currentChannelId: string | null;
  currentProductId: string | null;
  /** v33-c. 그 채널이 바이패스인가 — 편성표가 **죽일 칸**을 이것으로 고른다 */
  inLive?: boolean;
  favorites: readonly string[];
  onToggleFavorite: (channelId: string) => void;
  /** 같은 채널·같은 항목이면 아무 일도 일어나지 않는다 — 판정은 `resolveEpgPick` 이 한다 */
  onPick: (channelId: string, productId: string, isEntryCard: boolean) => void;
  signedIn: boolean;
  onClose: () => void;
  /**
   * 지금 재생 중인 것에 대한 **사실**. 시계는 `EpgScreen` 이 스스로 갖는다 (v27).
   *
   * v29. **`null` 이면 아무것도 재생되고 있지 않다** — 채널 전환을 건너는 동안이 그것이다
   * (`ChannelGate` 가 그 순간에도 이 시트를 그린다).
   */
  now: {
    channelId: string;
    /** **함수 하나다** — 시트가 열려 있는 동안 `WatchScreen` 이 다시 그려지지 않는다 */
    getPlayhead: () => {
      productId: string | null;
      elapsedSec: number;
      inAdBreak: boolean;
      /** v33. 바이패스 중인가 — 그때 `elapsedSec` 은 **라이브의 초**다 (`docs/67` D25) */
      inLive: boolean;
      /** v33-d. 바이패스에서 돌아갈 콘텐츠 초(`LiveView.resumeAtSec`). 아니면 `null` */
      liveResumeAtSec: number | null;
      /** v35. 시청자가 멈췄는가 — 그동안 그 행은 흐르지 않는다(절대 규칙 73 ② · `docs/71` §2.6) */
      paused: boolean;
    } | null;
    /** v34 — `EpgScreen` 의 그 창(지금 채널의 라이브 상자 · 주소 없음) */
    getLiveSlots?: () => readonly LiveEventSlot[] | null;
    /** v39 — `EpgScreen` 의 그 목록(재생 중인 회차가 편성표에 없을 때 · `docs/81` §5.3) */
    items?: readonly PlaylistItem[];
  } | null;
}) {
  const t = useT();

  return (
    /*
      `data-focus-scope` — 리모컨 포커스를 이 시트 안에 가둔다 (절대 규칙 32).
      **표시가 렌더하는 자리에 있어야 요소가 옮겨 다녀도 따라간다** (`data-i18n` 과 같은 방침).

      ★ **여기가 이 확장의 유일한 층이다** — 행에도 스크롤러에도 새 `data-focus-scope` 를
      만들지 않는다 (절대 규칙 65). 붙이면 `docs/25` §5 의 표와 K3 예외가 통째로 다시 열린다.
    */
    <div className="sheet" data-focus-scope>
      <div className="sheet__panel sheet__panel--wide">
        <div className="sheet__head">
          <h2 className="sheet__title">{t('epg.title')}</h2>
          <button type="button" className="sheet__close" data-focus-back onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        <EpgScreen
          rows={rows}
          currentChannelId={currentChannelId}
          currentProductId={currentProductId}
          inLive={inLive}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
          onPick={onPick}
          signedIn={signedIn}
          /* 시트에는 「방송 보기」가 없다 — 닫기가 그 일을 한다 */
          onBack={null}
          bare
          now={now}
        />
      </div>
    </div>
  );
}
